import React, { useEffect, useState, useCallback } from "react";
import { Container, Form, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import { localDateString, offsetDays, todayLocal } from "../utils/dateTz";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildDayKeys = (n) => {
  const days = [];
  for (let i = 0; i < n; i++) days.push(offsetDays(-i));
  return days;
};

const fmtDay = (key) => {
  const d = new Date(key + "T12:00:00");
  const today = todayLocal();
  const yesterday = offsetDays(-1);
  const label = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (key === today) return `${label}  (Today)`;
  if (key === yesterday) return `${label}  (Yesterday)`;
  return label;
};

export default function CashTracker() {
  const [dayAmounts, setDayAmounts] = useState({});
  const [inputs, setInputs] = useState({});
  const [notes, setNotes] = useState({});
  const [dayFlags, setDayFlags] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const dayKeys = buildDayKeys(15);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}api/cash-tracker`, { params: { range: "30d" } });
      if (!data.success) throw new Error(data.message);

      const map = {};
      const noteMap = {};
      let sum = 0;
      for (const r of data.records) {
        if (r.type === "pickup") {
          const key = localDateString(new Date(r.date));
          map[key] = r.amount;
          noteMap[key] = r.note || "";
          sum += r.amount;
        }
      }
      setDayAmounts(map);
      setTotal(sum);
      setDayFlags(data.dayFlags || {});

      const inp = {};
      const nts = {};
      for (const dk of dayKeys) {
        inp[dk] = map[dk] !== undefined ? String(map[dk]) : "";
        nts[dk] = noteMap[dk] || "";
      }
      setInputs(inp);
      setNotes(nts);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveDay = async (dayKey) => {
    const val = inputs[dayKey];
    if ((val === "" || val === undefined) && dayAmounts[dayKey] === undefined) return;
    const amount = val === "" || val === undefined ? 0 : parseFloat(val);
    if (isNaN(amount) || amount < 0) return;
    if (dayAmounts[dayKey] !== undefined && amount === dayAmounts[dayKey]) return;

    setSavingKey(dayKey);
    try {
      const { data } = await axios.post(`${API_URL}api/cash-tracker`, {
        type: "pickup",
        amount,
        date: dayKey,
        note: notes[dayKey] || "",
      });
      if (!data.success) throw new Error(data.message);
      toast.success(`Saved ${fmtDay(dayKey)}: ${fmt(amount)}`, { duration: 1500 });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const saveNote = async (dayKey) => {
    // Only save note if there's already a pickup amount for this day
    if (dayAmounts[dayKey] === undefined) return;
    setSavingKey(dayKey);
    try {
      const { data } = await axios.post(`${API_URL}api/cash-tracker`, {
        type: "pickup",
        amount: dayAmounts[dayKey],
        date: dayKey,
        note: notes[dayKey] || "",
      });
      if (!data.success) throw new Error(data.message);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const handleToggleDay = async (dayKey, field) => {
    try {
      await axios.patch(`${API_URL}api/cash-tracker/day-toggle`, { date: dayKey, field });
      fetchData();
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  return (
    <Container className="mt-4 mb-5" style={{ maxWidth: 720 }}>
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      <h2 style={{ fontWeight: 700, marginBottom: 6 }}>💵 Cash Pickup</h2>
      <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
        Enter the cash picked up each day. Auto-saves on blur.
      </p>

      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "18px 22px",
          border: "1px solid #e5e7eb",
          marginBottom: 20,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>
          Total (Last 30 Days)
        </div>
        <div style={{ fontSize: "1.7rem", fontWeight: 700, color: "#059669" }}>
          {loading ? <Spinner size="sm" animation="border" /> : fmt(total)}
        </div>
      </div>

      <div className="card p-0">
        <Table bordered size="sm" className="mb-0" style={{ fontSize: "0.9rem" }}>
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: 140 }}>Day</th>
              <th style={{ textAlign: "center", width: 60 }}>Picked</th>
              <th style={{ textAlign: "center", width: 70 }}>Deposited</th>
              <th style={{ textAlign: "right", width: 120 }}>Pickup $</th>
              <th style={{ minWidth: 110 }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {dayKeys.map((dk) => {
              const isToday = dk === todayLocal();
              const isSaving = savingKey === dk;
              const flags = dayFlags[dk] || {};
              return (
                <tr key={dk} style={isToday ? { background: "#fefce8" } : {}}>
                  <td style={{ fontWeight: isToday ? 700 : 500, verticalAlign: "middle" }}>
                    {fmtDay(dk)}
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!flags.picked}
                      onChange={() => handleToggleDay(dk, "picked")}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!flags.deposited}
                      onChange={() => handleToggleDay(dk, "deposited")}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                  <td style={{ padding: "4px 8px" }}>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={inputs[dk] || ""}
                        onChange={(e) =>
                          setInputs((prev) => ({ ...prev, [dk]: e.target.value }))
                        }
                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                        onBlur={() => saveDay(dk)}
                        disabled={isSaving}
                        style={{
                          textAlign: "right",
                          fontWeight: 600,
                          paddingRight: isSaving ? 30 : undefined,
                        }}
                      />
                      {isSaving && (
                        <Spinner
                          size="sm"
                          animation="border"
                          style={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 14,
                            height: 14,
                          }}
                        />
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "4px 8px" }}>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="note…"
                      value={notes[dk] || ""}
                      onChange={(e) =>
                        setNotes((prev) => ({ ...prev, [dk]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                      onBlur={() => saveNote(dk)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Container>
  );
}
